import { fillPath, route } from "./routes.js";
import { z } from "zod";
import { scalingEventPageSchema, scalingEventQuerySchema } from "@zion/sdk-contracts";
//#region src/apps.ts
/**
* `GET /v1/apps/{appId}/scaling-events` — one app's scaling timeline.
*
* Producer `ScalingEventsController` in core-api, under `src/scaling-events/`. The path sits
* outside `/v1/admin`, so this file writes it literally and uses no prefix constant. The only
* caller is an admin page, which is why the route lives in this package.
*
* Authorization is `RequireAppIdParamPermission('apps:read', 'appId')`, the app-plane guard: it
* matches `{appId}` against the caller's own app grant and the subject filter is derived from that
* authorized identity, never from the raw param. So `{appId}` is authorized server-side and is not
* a trusted cross-app selector.
*
* Both the query and the page shape are owned by `@zion/sdk-contracts` (`scaling-event.ts`), so
* this file declares the route, the path builder and the naming aliases only.
*
* Two facts the shell's `api/client.ts` obscures, and the controller is the fact. The wire
* response is `ScalingEventPage` — `{ events, nextSince?, truncated }` over raw envelopes — and
* the shell's `AppScalingEventsPage` is a client-side projection it builds with
* `toScalingEventView`, never a wire shape. And `since` is `z.coerce.number().int().min(0)`, the
* JetStream sequence, while the shell passes it as a string; a numeric string coerces, and
* anything else is a 400.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client.
* No shape in this file travels in both directions.
* Both shapes come from `@zion/sdk-contracts`, which stays their owner. The response reads
* {@link scalingEventPageViewSchema}, a `z.object(scalingEventPageSchema.shape)` rebuild (R-38);
* the query is a request and keeps the upstream's strictness. A test pins the view's key set
* against its upstream.
*/
/** One app's timeline, in OpenAPI `{param}` form. Fill it with {@link appScalingEventsPath}. */
const APP_SCALING_EVENTS_PATH_TEMPLATE = "/v1/apps/{appId}/scaling-events";
/**
* `GET /v1/apps/{appId}/scaling-events`. A bounded page of node-provisioning, pod-lifecycle,
* compute-unit-provisioning and clamp events for one app. App-plane permission `apps:read` on
* `{appId}`; 404 when the app scope cannot be resolved.
*/
const LIST_APP_SCALING_EVENTS_ROUTE = route("GET", APP_SCALING_EVENTS_PATH_TEMPLATE);
/**
* Fill {@link APP_SCALING_EVENTS_PATH_TEMPLATE} with one app id.
*
* @param appId the `release.apps_catalog` uuid the guard matches against the caller's
*   `appPermissions[]` tuple. Not the slug. Throws when it is empty, longer than 128 characters,
*   or carries a character outside the path-param grammar.
* @returns the request path, each segment percent-encoded.
*/
function appScalingEventsPath(appId) {
	return fillPath(APP_SCALING_EVENTS_PATH_TEMPLATE, { appId });
}
/**
* `GET /v1/apps/{appId}/scaling-events` query. Owned by `@zion/sdk-contracts`
* (`scalingEventQuerySchema`): `since` is the last JetStream sequence the client has seen and the
* server replays after it, `limit` is capped at 1000 with the server's own default when omitted,
* and `class` narrows to one event class.
*/
const listAppScalingEventsRequestSchema = scalingEventQuerySchema;
/**
* The page shape, relaxed to strip an unknown member (R-38): raw envelopes, the cursor for the next
* page, and `truncated` meaning more events exist past `nextSince`.
*
* `scalingEventPageSchema` in `@zion/sdk-contracts` (`scaling-event.ts`) owns this shape and stays
* its home. This is `z.object(upstream.shape)`, with no `.refine()` upstream to lose, and
* `apps.test.ts` pins the two key sets equal.
*
* ⚠️ The relaxation is at the page level only. Each element is `scalingEventEnvelopeSchema`, an
* intersection that already accepts and strips an unknown member — the upstream defect this
* package's test records. From zod 4.6 it runs the body's `class === payload.type` refinement.
*/
const scalingEventPageViewSchema = z.object(scalingEventPageSchema.shape);
/** `GET /v1/apps/{appId}/scaling-events` response. */
const listAppScalingEventsResponseSchema = scalingEventPageViewSchema;
/** Every route this file declares, for the package's route census. */
const appsRoutes = Object.freeze([LIST_APP_SCALING_EVENTS_ROUTE]);
//#endregion
export { APP_SCALING_EVENTS_PATH_TEMPLATE, LIST_APP_SCALING_EVENTS_ROUTE, appScalingEventsPath, appsRoutes, listAppScalingEventsRequestSchema, listAppScalingEventsResponseSchema, scalingEventPageViewSchema };

//# sourceMappingURL=apps.js.map